import { useState, useEffect } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { useChatStore } from '@/stores/chatStore';
import { useSocketConnection } from '@/hooks/useSocketConnection';
import { useOAuthCallback } from '@/hooks/useOAuthCallback';
import { SnackbarContainer } from '@/components/Snackbar/Snackbar';
import { Logo } from '@/components/Logo';
import { UserMenu } from '@/components/UserMenu';
import { ChatModal } from '@/components/Chat/ChatModal';
import { FloatingActions } from '@/components/FloatingActions';
import { WelcomeModal, hasSeenWelcomeModal } from '@/components/WelcomeModal';
import { LeaderboardModal } from '@/components/LeaderboardModal';
import { WelcomeModalProvider } from '@/contexts/WelcomeModalContext';
import { EmailVerificationBanner } from '@/components/EmailVerificationBanner';
import { BugReportModal } from '@/components/BugReportModal';
import { useUiStore } from '@/stores/uiStore';

// Hamburger icon for mobile menu
function MenuIcon({ open }: { open: boolean }) {
  return (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
      {open ? (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M6 18L18 6M6 6l12 12"
        />
      ) : (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M4 6h16M4 12h16M4 18h16"
        />
      )}
    </svg>
  );
}

export function Layout() {
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [bugReportOpen, setBugReportOpen] = useState(false);
  const chatOpen = useUiStore((state) => state.chatOpen);
  const leaderboardOpen = useUiStore((state) => state.leaderboardOpen);
  const setChatOpen = useUiStore((state) => state.setChatOpen);
  const setLeaderboardOpen = useUiStore((state) => state.setLeaderboardOpen);
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);
  const clearError = useAuthStore((state) => state.clearError);
  const hasUnreadChat = useChatStore((state) => state.hasUnread);
  const setChatModalOpen = useChatStore((state) => state.setModalOpen);
  const location = useLocation();

  // Keep chat store in sync so unread is only set when modal is closed
  useEffect(() => {
    setChatModalOpen(chatOpen);
  }, [chatOpen, setChatModalOpen]);

  // When user returns to this tab and chat is open, clear unread
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && chatOpen) {
        useChatStore.getState().setHasUnread(false);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [chatOpen]);

  // Tab title: show unread indicator when chat has new messages and modal is closed
  const TAB_UNREAD_PREFIX = '● ';
  useEffect(() => {
    if (hasUnreadChat) {
      if (!document.title.startsWith(TAB_UNREAD_PREFIX)) {
        document.title = TAB_UNREAD_PREFIX + document.title;
      }
    } else {
      if (document.title.startsWith(TAB_UNREAD_PREFIX)) {
        document.title = document.title.slice(TAB_UNREAD_PREFIX.length);
      }
    }
  }, [hasUnreadChat]);

  // Re-apply unread prefix after navigation (Helmet may have set a new title)
  useEffect(() => {
    if (hasUnreadChat && !document.title.startsWith(TAB_UNREAD_PREFIX)) {
      document.title = TAB_UNREAD_PREFIX + document.title;
    }
  }, [hasUnreadChat, location.pathname]);

  // Check if user has seen welcome modal on mount
  useEffect(() => {
    if (!hasSeenWelcomeModal()) {
      setWelcomeOpen(true);
    }
  }, []);

  // Manage socket connection at app level
  useSocketConnection();

  // Handle OAuth callback (token in URL)
  useOAuthCallback();

  const isActive = (path: string) => location.pathname === path;
  const isGalleryCanvas = location.pathname.startsWith('/gallery/');
  const isAdminPanel = location.pathname.startsWith('/admin-panel/');

  // Close mobile menu on navigation
  const closeMobileMenu = () => setMobileMenuOpen(false);

  const navLinkClass = (path: string) =>
    `flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-colors ${
      isActive(path)
        ? 'bg-wall-accent/20 text-wall-accent'
        : 'text-wall-text hover:bg-wall-border/30'
    }`;

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header: on mobile = logo + hamburger only (like Twitter, LinkedIn, etc.) */}
      <header className="shrink-0 bg-wall-surface/80 backdrop-blur-md border-b border-wall-border/50 px-3 sm:px-4 py-2 sm:py-3 z-20">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-6 min-w-0">
            <Logo />

            {/* Desktop nav - hidden on mobile */}
            <nav className="hidden sm:flex gap-2">
              <Link
                to="/"
                className={`px-3 sm:px-4 py-1.5 rounded-lg font-medium transition-all duration-200 ${
                  isActive('/')
                    ? 'bg-linear-to-r from-wall-accent to-wall-accent-secondary text-white shadow-glow'
                    : 'text-wall-muted hover:text-wall-text hover:bg-wall-border/50'
                }`}
                title="Canvas"
              >
                Canvas
              </Link>
              <Link
                to="/gallery"
                className={`px-3 sm:px-4 py-1.5 rounded-lg font-medium transition-all duration-200 ${
                  isActive('/gallery')
                    ? 'bg-linear-to-r from-wall-accent to-wall-accent-secondary text-white shadow-glow'
                    : 'text-wall-muted hover:text-wall-text hover:bg-wall-border/50'
                }`}
                title="Gallery"
              >
                Gallery
              </Link>
            </nav>
          </div>

          {/* Desktop: auth or UserMenu. Mobile: hamburger only */}
          <div className="hidden sm:flex items-center gap-4 shrink-0">
            {user ? (
              <UserMenu />
            ) : (
              <>
                <Link
                  to="/login"
                  className="px-4 py-1.5 text-wall-muted hover:text-wall-text transition-colors rounded"
                >
                  Login
                </Link>
                <Link
                  to="/register"
                  onClick={clearError}
                  className="px-4 py-1.5 bg-linear-to-r from-wall-accent to-wall-accent-secondary hover:opacity-90 rounded-lg font-medium shadow-glow transition-all duration-200"
                >
                  Register
                </Link>
              </>
            )}
          </div>

          {/* Mobile: hamburger - only when we have menu content (nav or auth) */}
          <button
            type="button"
            onClick={() => setMobileMenuOpen((o) => !o)}
            className="sm:hidden p-2 -mr-2 text-wall-muted hover:text-wall-text rounded-lg hover:bg-wall-border/30 transition-colors"
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileMenuOpen}
          >
            <MenuIcon open={mobileMenuOpen} />
          </button>
        </div>
      </header>

      {/* Mobile menu: slide-over (like major apps) */}
      <>
        <div
          role="presentation"
          className={`sm:hidden fixed inset-0 bg-black/50 z-30 transition-opacity duration-200 ${
            mobileMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
          onClick={closeMobileMenu}
          aria-hidden
        />
        <aside
          className={`sm:hidden fixed top-0 right-0 bottom-0 w-full max-w-[280px] bg-wall-surface/95 backdrop-blur-xl border-l border-wall-border/40 shadow-2xl z-40 flex flex-col transition-transform duration-200 ease-out ${
            mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
          aria-modal="true"
          aria-label="Navigation"
        >
          {/* Single close control, no duplicate logo — main header stays visible behind overlay */}
          <div className="flex justify-end shrink-0 pt-3 pr-3">
            <button
              type="button"
              onClick={closeMobileMenu}
              className="p-2 -mr-1 text-wall-muted hover:text-wall-text rounded-full hover:bg-wall-border/30 transition-colors"
              aria-label="Close menu"
            >
              <MenuIcon open />
            </button>
          </div>
          <nav className="flex flex-col px-4 pb-6 gap-0.5 flex-1 overflow-auto">
            <Link to="/" onClick={closeMobileMenu} className={navLinkClass('/')}>
              <svg
                className="w-5 h-5 shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14"
                />
              </svg>
              Canvas
            </Link>
            <Link to="/gallery" onClick={closeMobileMenu} className={navLinkClass('/gallery')}>
              <svg
                className="w-5 h-5 shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                />
              </svg>
              Gallery
            </Link>
            {user ? (
              <>
                <div className="my-2 border-t border-wall-border/50" />
                <div className="flex items-center gap-3 px-4 py-3">
                  {user.profilePicture ? (
                    <img
                      src={user.profilePicture}
                      alt=""
                      className="w-12 h-12 rounded-full object-cover border-2 border-wall-accent/40 shrink-0"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-linear-to-br from-wall-accent to-wall-accent-secondary flex items-center justify-center text-sm font-bold text-white shrink-0">
                      {user.username
                        .split(' ')
                        .map((n) => n[0])
                        .join('')
                        .toUpperCase()
                        .slice(0, 2)}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-wall-text truncate">{user.username}</p>
                    <p className="text-xs text-wall-muted truncate">{user.email}</p>
                  </div>
                </div>
                <Link to="/profile" onClick={closeMobileMenu} className={navLinkClass('/profile')}>
                  <svg
                    className="w-5 h-5 shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                  Profile
                </Link>
                <button
                  type="button"
                  onClick={() => {
                    closeMobileMenu();
                    setBugReportOpen(true);
                  }}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg font-medium text-wall-text hover:bg-wall-border/30 transition-colors"
                >
                  <svg
                    className="w-5 h-5 shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <rect x="3" y="5" width="18" height="14" rx="2" strokeWidth={2} />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="m3 7 9 6 9-6"
                    />
                  </svg>
                  Report a bug
                </button>
                {user.adminRole && ['SUPER_ADMIN', 'ADMIN'].includes(user.adminRole) && (
                  <>
                    <Link
                      to="/admin-panel/reports"
                      onClick={closeMobileMenu}
                      className={navLinkClass('/admin-panel/reports')}
                    >
                      <svg
                        className="w-5 h-5 shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12h6m-6 4h4m5 4H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h7l5 5v9a2 2 0 0 1-2 2Z"
                        />
                      </svg>
                      Bug Reports
                    </Link>
                    <Link
                      to="/admin-panel/users"
                      onClick={closeMobileMenu}
                      className={navLinkClass('/admin-panel/users')}
                    >
                      <svg
                        className="w-5 h-5 shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 4v16m8-8H4"
                        />
                      </svg>
                      Users
                    </Link>
                  </>
                )}
                <button
                  type="button"
                  onClick={() => {
                    closeMobileMenu();
                    logout();
                  }}
                  className="flex items-center gap-3 w-full px-4 py-3 rounded-lg text-left font-medium text-red-400 hover:bg-red-500/10 transition-colors"
                >
                  <svg
                    className="w-5 h-5 shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                    />
                  </svg>
                  Logout
                </button>
              </>
            ) : (
              <>
                <div className="my-2 border-t border-wall-border/50" />
                <div className="p-3 space-y-2">
                  <Link
                    to="/login"
                    onClick={closeMobileMenu}
                    className="flex items-center justify-center w-full py-3 rounded-xl font-medium text-wall-text border border-wall-border/60 hover:border-wall-border hover:bg-wall-border/10 transition-colors"
                  >
                    Log in
                  </Link>
                  <Link
                    to="/register"
                    onClick={() => {
                      clearError();
                      closeMobileMenu();
                    }}
                    className="flex items-center justify-center w-full py-3 rounded-xl font-medium text-white bg-linear-to-r from-wall-accent to-wall-accent-secondary hover:opacity-95 transition-opacity"
                  >
                    Create account
                  </Link>
                </div>
              </>
            )}
          </nav>
        </aside>
      </>

      <EmailVerificationBanner />

      {/* Main Content */}
      <WelcomeModalProvider openWelcomeModal={() => setWelcomeOpen(true)}>
        <main className="flex-1 overflow-hidden">
          <Outlet />
        </main>
      </WelcomeModalProvider>

      {/* Snackbar notifications */}
      <SnackbarContainer />

      {/* Floating actions: Leaderboard + Chat (hidden on gallery canvas so Comments Send is visible) */}
      {!chatOpen && !isGalleryCanvas && !isAdminPanel && (
        <div className={location.pathname === '/' ? 'max-[1040px]:hidden' : ''}>
          <FloatingActions
            onOpenChat={() => setChatOpen(true)}
            onOpenLeaderboard={() => setLeaderboardOpen(true)}
            hasUnreadChat={hasUnreadChat}
          />
        </div>
      )}
      <ChatModal isOpen={chatOpen} onClose={() => setChatOpen(false)} />

      {/* Welcome Modal */}
      <WelcomeModal isOpen={welcomeOpen} onClose={() => setWelcomeOpen(false)} />

      {/* Leaderboard Modal */}
      <LeaderboardModal isOpen={leaderboardOpen} onClose={() => setLeaderboardOpen(false)} />

      <BugReportModal isOpen={bugReportOpen} onClose={() => setBugReportOpen(false)} />
    </div>
  );
}
